import { useTranslation } from 'react-i18next';
import type { UserSettings } from '@/api/auth';
import type { CasinoBonusWalletSessionError } from '@/domain/casino';
import { formatAmount, formatMainBalance } from '@/domain/player-balance';
import { Button, Modal } from '@/ui/common';

export interface CasinoBonusWalletModalProps {
  open: boolean;
  user: UserSettings | null | undefined;
  /** When set from a session error, hides the conflicting wallet option. */
  sessionError?: CasinoBonusWalletSessionError | null;
  onClose: () => void;
  onSelectMain: () => void;
  onSelectBonus: () => void;
}

/**
 * Legacy `#casinoBonusModal` — pick main vs casino bonus wallet before launch.
 */
export function CasinoBonusWalletModal({
  open,
  user,
  sessionError = null,
  onClose,
  onSelectMain,
  onSelectBonus,
}: CasinoBonusWalletModalProps) {
  const { t } = useTranslation();
  const hideMain = sessionError === 'InGameBonusCreditsBonusError';
  const hideBonus = sessionError === 'InGameBonusCreditsError';

  const mainLabel = formatMainBalance(user);
  const bonusCredits = user?.Credits?.CasinoBonusCredits ?? 0;
  const decimals =
    typeof user?.NumberOfDecimals === 'number' && user.NumberOfDecimals >= 0
      ? user.NumberOfDecimals
      : 2;
  const currency =
    user?.Currency?.trim() ||
    user?.CurrCode?.trim() ||
    user?.CurrencyCode?.trim() ||
    '';
  const bonusFormatted = currency
    ? `${formatAmount(bonusCredits, decimals)} ${currency}`
    : formatAmount(bonusCredits, decimals);

  const showMain = !hideMain;
  const showBonus = !hideBonus;

  return (
    <Modal
      open={open}
      dismissible={false}
      onClose={onClose}
      aria-labelledby="casino-bonus-wallet-title"
      panelClassName="w-full max-w-lg rounded-lg border border-border-subtle bg-bg-surface p-5 shadow-lg"
    >
      <div className="flex flex-col gap-5">
        <h2
          id="casino-bonus-wallet-title"
          className="text-center text-heading-s font-bold uppercase text-text-primary"
        >
          {t('SelectYourWallet', { defaultValue: 'Select your wallet' })}
        </h2>

        <div className="flex w-full items-stretch gap-3">
          {showMain ? (
            <div className="flex min-w-0 flex-1 flex-col items-center gap-3 rounded-md border border-border-subtle bg-bg-elevated p-4">
              <p className="text-heading-s font-bold text-text-primary">{mainLabel}</p>
              <Button
                variant="primary"
                fullWidth
                onClick={onSelectMain}
                disabled={hideMain}
              >
                {t('MainBalance', { defaultValue: 'Main Balance' })}
              </Button>
            </div>
          ) : null}

          {showMain && showBonus ? (
            <div className="w-px shrink-0 self-stretch bg-border-subtle" aria-hidden />
          ) : null}

          {showBonus ? (
            <div className="flex min-w-0 flex-1 flex-col items-center gap-3 rounded-md border border-border-subtle bg-bg-elevated p-4">
              <p className="text-heading-s font-bold text-text-primary">
                {bonusFormatted}
              </p>
              <Button
                variant="primary"
                fullWidth
                onClick={onSelectBonus}
                disabled={hideBonus}
              >
                {t('CasinoBonusBalance', { defaultValue: 'Casino Bonus Balance' })}
              </Button>
            </div>
          ) : null}
        </div>

        <Button variant="secondary" fullWidth onClick={onClose}>
          {t('Close', { defaultValue: 'Close' })}
        </Button>
      </div>
    </Modal>
  );
}
